/**
 * LiveRoom — holds the active co-driven-demo room (code + this browser's role) in React
 * state at the app root. LiveEntry (host) and LiveJoin (guest) start it; LiveSession
 * runs the sync engine + chrome while it is active.
 *
 * There is no "control holder": both people can click anything at any time. Two clicks
 * that collide resolve last-click-wins (sync-core), and the shared store starts fresh
 * with every room.
 *
 * In the static build no route ever calls startRoom, so `room` stays null and nothing
 * live renders — this provider is inert.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { disableRelay, enableRelay } from "../../shims/socket-client";
import { enterSyncRoom, leaveSyncRoom } from "./useSyncedState";
import type { LiveRole } from "./live";

interface Room {
  code: string;
  role: LiveRole;
}

interface LiveRoomValue {
  room: Room | null;
  startRoom: (code: string, role: LiveRole) => void;
  endRoom: () => void;
}

const Ctx = createContext<LiveRoomValue | null>(null);

export function LiveRoomProvider({ children }: { children: ReactNode }) {
  const [room, setRoom] = useState<Room | null>(null);
  const value = useMemo<LiveRoomValue>(
    () => ({
      room,
      startRoom: (code, role) => {
        // Flip the socket singleton from same-tab loopback to the relay transport.
        // ONLY here: ordinary demo visitors never open a network connection, and
        // their constant-code BLS room ("demo-session") can never collide across
        // strangers because it stays loopback for them.
        enableRelay();
        enterSyncRoom(code, role);
        setRoom({ code, role });
      },
      endRoom: () => {
        // Back to loopback so a guest who keeps exploring after the walkthrough has
        // a working session mirror + check-in flow (relay frames would go nowhere).
        disableRelay();
        leaveSyncRoom();
        setRoom(null);
      },
    }),
    [room],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLiveRoom(): LiveRoomValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLiveRoom must be used within <LiveRoomProvider>");
  return ctx;
}
